function sansAnomalie(valeur) {
    return { valeur, anomalies: [] };
}
function avecAnomalie(valeur, champ, valeurSource, motif, gravite = 'REJET') {
    return {
        valeur,
        anomalies: [{ champ, valeurSource: texteBrut(valeurSource), motif, gravite }],
    };
}
/** La valeur telle qu'on la montrera dans le journal des rejets. */
function texteBrut(valeur) {
    if (valeur === null || valeur === undefined)
        return '';
    // Une date invalide se journalise telle quelle : `toISOString` lèverait,
    // et un classeur entier deviendrait illisible pour une seule cellule.
    if (valeur instanceof Date) {
        return Number.isNaN(valeur.getTime()) ? 'date invalide' : valeur.toISOString().slice(0, 10);
    }
    return String(valeur);
}
function texte(valeur) {
    const t = texteBrut(valeur).trim();
    return t === '' ? null : t;
}
// ─────────────────────────────────────────────────────────────────────
//  Matricule
// ─────────────────────────────────────────────────────────────────────
const CIVILITES = new Set(['M', 'MR', 'MME', 'MLLE', 'MELLE', 'MONSIEUR', 'MADAME', 'MADEMOISELLE']);
/**
 * TEXT, jamais un nombre.
 *
 * 20 % des matricules du corpus sont alphanumériques (`2501B`, `58/1024`),
 * et les zéros de tête sont significatifs : `007` et `7` sont deux
 * personnes. Un tableur qui lit la colonne en numérique rend `7` et un
 * `.0` traînant — on retire le second sans toucher au premier, qu'on ne
 * peut de toute façon plus retrouver.
 */
export function normaliserMatricule(brut) {
    const t = texte(brut);
    if (t === null)
        return null;
    // Une civilité dans la colonne du matricule (5 fichiers du corpus) : ce
    // n'est pas un identifiant, et la garder ferait de tous les « Mme » une
    // seule personne.
    if (CIVILITES.has(t.toUpperCase().replace(/[.\s]/g, '')))
        return null;
    // 123.0 → 123, mais 12.5 reste 12.5 : ce n'est pas à nous d'arrondir.
    return /^-?\d+\.0+$/.test(t) ? t.replace(/\.0+$/, '') : t;
}
// ─────────────────────────────────────────────────────────────────────
//  Dates
// ─────────────────────────────────────────────────────────────────────
/** Époque des numéros de série Excel — 30/12/1899, bogue de 1900 inclus. */
const EPOQUE_EXCEL_MS = Date.UTC(1899, 11, 30);
const MS_PAR_JOUR = 86_400_000;
/**
 * Bornes de vraisemblance.
 *
 * Le plancher est à 61 et non à 1 : Excel croit que 1900 est bissextile et
 * compte un 29 février qui n'a jamais existé. L'époque du 30/12/1899 corrige
 * ce décalage — mais seulement au-delà du série 60. En deçà, on parlerait
 * de janvier ou février 1900 : aucune date de naissance ni d'embauche
 * plausible. On rejette plutôt que de rendre une date fausse d'un jour.
 */
const SERIE_MIN = 61;
const SERIE_MAX = 2_958_465; // 31/12/9999
export function dateDepuisSerieExcel(serie) {
    if (!Number.isFinite(serie) || serie < SERIE_MIN || serie > SERIE_MAX)
        return null;
    return new Date(EPOQUE_EXCEL_MS + Math.round(serie) * MS_PAR_JOUR);
}
/** Une date réelle au calendrier — écarte le 31 février et le 13e mois. */
function construireDate(annee, mois, jour) {
    if (mois < 1 || mois > 12 || jour < 1 || jour > 31)
        return null;
    const d = new Date(Date.UTC(annee, mois - 1, jour));
    const reelle = d.getUTCFullYear() === annee && d.getUTCMonth() === mois - 1 && d.getUTCDate() === jour;
    return reelle ? d : null;
}
/**
 * Trois formats, dans cet ordre : `JJ/MM/AAAA`, `JJ-MM-AAAA`, `AAAA-MM-JJ`.
 *
 * **Jamais de format américain.** `03/04/2026` est le 3 avril à Madagascar.
 * Accepter `MM/JJ/AAAA` en repli rendrait la lecture dépendante de la
 * valeur : les dates ambiguës passeraient en français et les autres en
 * américain, sans que personne ne le voie.
 *
 * Une année sur deux chiffres est **refusée**, pas complétée : `62` peut
 * être 1962 pour une date de naissance et 2062 pour rien du tout.
 */
export function normaliserDate(brut, champ, options = {}) {
    if (brut === null || brut === undefined || brut === '')
        return sansAnomalie(null);
    if (brut instanceof Date) {
        if (Number.isNaN(brut.getTime()))
            return avecAnomalie(null, champ, brut, 'DATE_ILLISIBLE');
        return dansLeCalendrier(brut)
            ? sansAnomalie(brut)
            : avecAnomalie(null, champ, brut, 'DATE_IMPOSSIBLE');
    }
    if (typeof brut === 'number') {
        const d = dateDepuisSerieExcel(brut);
        if (d === null)
            return avecAnomalie(null, champ, brut, 'DATE_ILLISIBLE');
        return dansLeCalendrier(d)
            ? sansAnomalie(d)
            : avecAnomalie(null, champ, brut, 'DATE_IMPOSSIBLE');
    }
    const t = texte(brut);
    if (t === null)
        return sansAnomalie(null);
    // B2 (revue S69) — écrite en texte aussi, complétée ou inversée comprise,
    // une date hors de 1900-2100 est refusée.
    const lue = dateDuTexte(t, brut, champ, options);
    return lue.valeur === null || dansLeCalendrier(lue.valeur)
        ? lue
        : avecAnomalie(null, champ, brut, 'DATE_IMPOSSIBLE');
}
function dateDuTexte(t, brut, champ, options) {
    // Un nombre écrit en texte reste un numéro de série Excel.
    if (/^\d+(\.\d+)?$/.test(t)) {
        const d = dateDepuisSerieExcel(Number(t));
        if (d !== null)
            return sansAnomalie(d);
    }
    const enLettres = dateEnLettres(t);
    const separe = /^(\d{1,4})[/\-.](\d{1,2})[/\-.](\d{1,4})$/.exec(t);
    if (separe === null && enLettres === null)
        return avecAnomalie(null, champ, brut, 'DATE_ILLISIBLE');
    const [a, b, c] = enLettres ?? separe?.slice(1, 4);
    // AAAA-MM-JJ : reconnaissable sans ambiguïté à son premier groupe.
    if (a.length === 4) {
        const d = construireDate(Number(a), Number(b), Number(c));
        return d === null ? avecAnomalie(null, champ, brut, 'DATE_IMPOSSIBLE') : sansAnomalie(d);
    }
    // R8 (16/10) — l'année sur deux chiffres se complète quand un seul siècle
    // est plausible ; la valeur se propose, signalée, et la chargée confirme.
    // Sur un, trois chiffres (« 202 » pour 2020) : illisible, jamais complétée (B2).
    if (c.length !== 2 && c.length !== 4)
        return avecAnomalie(null, champ, brut, 'DATE_ILLISIBLE');
    const completee = c.length === 2;
    const annee = completee ? siecle(Number(c), champ, options.aujourdHui) : Number(c);
    if (annee === null)
        return avecAnomalie(null, champ, brut, 'ANNEE_SUR_DEUX_CHIFFRES');
    // Décision du TL (16/10) — jour et mois inversés : toute la feuille écrit
    // MM/JJ (`ordreDesDates`), ou cette date n'existe qu'inversée. Proposée, signalée.
    const directe = construireDate(annee, Number(b), Number(a));
    const inversee = enLettres === null ? construireDate(annee, Number(a), Number(b)) : null;
    const moisJour = options.moisJour === true && inversee !== null;
    if (moisJour || (directe === null && inversee !== null)) {
        return avecAnomalie(inversee, champ, brut, 'JOUR_MOIS_INVERSES', 'IMPRECISION');
    }
    if (directe !== null) {
        return completee
            ? avecAnomalie(directe, champ, brut, 'ANNEE_COMPLETEE', 'IMPRECISION')
            : sansAnomalie(directe);
    }
    return avecAnomalie(null, champ, brut, 'DATE_IMPOSSIBLE');
}
/**
 * D5 (rejeu du 17/10) — une cellule date en l'an 52020 passait la lecture
 * puis faisait échouer l'application : la base n'écrit pas une telle date.
 * Hors de 1900-2100, aucune date d'un salarié n'est vraie.
 */
function dansLeCalendrier(d) {
    const annee = d.getUTCFullYear();
    return annee >= 1900 && annee <= 2100;
}
/** Âge plausible d'un salarié à sa naissance complétée (R8). */
const AGE_MIN_SIECLE = 16;
const AGE_MAX_SIECLE = 80;
/**
 * Le siècle d'une année sur deux chiffres, ou `null` si les deux restent
 * possibles. Naissance : celui qui donne un âge de 16 à 80 ans. Entrée,
 * départ : 20AA si l'année n'est pas dans le futur, sinon 19AA.
 */
function siecle(aa, champ, aujourdHui = new Date()) {
    const an = aujourdHui.getUTCFullYear();
    if (champ === 'dateNaissance') {
        const possibles = [1900 + aa, 2000 + aa].filter((x) => an - x >= AGE_MIN_SIECLE && an - x <= AGE_MAX_SIECLE);
        return possibles.length === 1 ? (possibles[0] ?? null) : null;
    }
    return 2000 + aa <= an ? 2000 + aa : 1900 + aa;
}
const MOIS_EN_LETTRES = [
    ['JANVIER', 'JANV', 'JAN'],
    ['FEVRIER', 'FEVR', 'FEV'],
    ['MARS', 'MAR'],
    ['AVRIL', 'AVR'],
    ['MAI'],
    ['JUIN'],
    ['JUILLET', 'JUIL'],
    ['AOUT'],
    ['SEPTEMBRE', 'SEPT', 'SEP'],
    ['OCTOBRE', 'OCT'],
    ['NOVEMBRE', 'NOV'],
    ['DECEMBRE', 'DEC'],
];
/** « 12 mars 85 », « 1er-sept-1990 » : [jour, mois, année] en chiffres, ou `null`. */
function dateEnLettres(t) {
    const n = t
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();
    const m = /^(\d{1,2})(?:ER)?[\s\-./]+([A-Z]+)\.?[\s\-./]+(\d{2}|\d{4})$/.exec(n);
    if (m === null)
        return null;
    const [, jour, mot, annee] = m;
    const rang = MOIS_EN_LETTRES.findIndex((noms) => noms.includes(mot));
    return rang < 0 ? null : [jour, String(rang + 1), annee];
}
/**
 * Décision du TL (16/10) — l'ordre des dates d'une colonne, feuille par
 * feuille. Une colonne où des dates n'existent qu'en MM/JJ (« 05/13/1980 »)
 * et aucune qu'en JJ/MM est écrite à l'américaine : toutes ses dates se
 * lisent alors mois d'abord — proposées, signalées, à confirmer.
 */
export function ordreDesDates(valeurs) {
    let jourEvident = 0;
    let moisEvident = 0;
    for (const v of valeurs) {
        if (typeof v !== 'string')
            continue;
        const m = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2}|\d{4})$/.exec(v.trim());
        if (m === null)
            continue;
        const a = Number(m[1]);
        const b = Number(m[2]);
        if (a > 12 && b <= 12)
            jourEvident++;
        if (b > 12 && a <= 12)
            moisEvident++;
    }
    return moisEvident >= 2 && jourEvident === 0 ? 'MOIS_JOUR' : 'JOUR_MOIS';
}
// ─────────────────────────────────────────────────────────────────────
//  Sexe
// ─────────────────────────────────────────────────────────────────────
// G7-P2 (19/10) — en malgache : `LAHY` (homme), `VAVY` (femme), et leurs
// initiales `L` / `V`, écrites par un DNS du corpus.
const MASCULIN = new Set(['M', 'H', 'G', 'MASCULIN', 'HOMME', 'GARCON', 'L', 'LAHY']);
const FEMININ = new Set(['F', 'FEMININ', 'FEMME', 'FILLE', 'V', 'VAVY']);
const CIVILITES_MASCULINES = new Set(['MONSIEUR', 'MR', 'M']);
const CIVILITES_FEMININES = new Set(['MADAME', 'MME', 'MADEMOISELLE', 'MLLE', 'MELLE']);
/**
 * Le corpus contient `M`, `F`, `MASCULIN`, `H`, `FEMININ`, `FEMME`, `G`,
 * mais aussi `X` et `C`. Ces deux derniers ne veulent rien dire ici : ils
 * viennent d'une colonne mal mappée. On rejette plutôt que de trancher.
 */
export function normaliserSexe(brut) {
    const t = texte(brut);
    if (t === null)
        return sansAnomalie(null);
    const cle = t
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();
    if (MASCULIN.has(cle))
        return sansAnomalie('M');
    if (FEMININ.has(cle))
        return sansAnomalie('F');
    // R6 (16/10) — une civilité dans la colonne du sexe le dit sans ambiguïté.
    const civilite = cle.replace(/[.\s]/g, '');
    if (CIVILITES_MASCULINES.has(civilite))
        return sansAnomalie('M');
    if (CIVILITES_FEMININES.has(civilite))
        return sansAnomalie('F');
    return avecAnomalie(null, 'sexe', brut, 'SEXE_INCONNU');
}
// ─────────────────────────────────────────────────────────────────────
//  Booléens et compteurs
// ─────────────────────────────────────────────────────────────────────
const FAUX = new Set(['0', 'NON', 'N', 'FALSE', 'FAUX', '']);
/**
 * `estAgent` — toute valeur non vide et différente de `0`/`NON`/`FALSE`
 * vaut `true`. Le corpus utilise `1`, `X`, `x`, `*`, `TRUE`, `'1'` : les
 * énumérer serait une course perdue.
 */
export function normaliserBooleen(brut) {
    if (brut === null || brut === undefined)
        return null;
    if (typeof brut === 'boolean')
        return brut;
    const t = texteBrut(brut).trim().toUpperCase();
    if (t === '')
        return null;
    return !FAUX.has(t);
}
/**
 * `nbConjoint` et `nbEnfant` — un nombre si la valeur en est un.
 *
 * `X` vaut « au moins un » : on retient 1 et **on le signale**. La
 * différence compte : une entreprise qui déclare `X` pour cinq enfants et
 * une qui en déclare un seul produiraient le même compteur, et le rapport
 * de différences afficherait un écart faux au prochain import.
 */
export function normaliserCompteur(brut, champ) {
    if (brut === null || brut === undefined)
        return sansAnomalie(null);
    if (typeof brut === 'number') {
        if (!Number.isFinite(brut) || brut < 0)
            return sansAnomalie(null);
        return borne(Math.trunc(brut), champ, brut);
    }
    const t = texteBrut(brut).trim().toUpperCase();
    if (t === '' || FAUX.has(t))
        return sansAnomalie(0);
    if (/^\d+$/.test(t))
        return borne(Number(t), champ, brut);
    if (t === 'TRUE' || t === 'VRAI')
        return sansAnomalie(1);
    return avecAnomalie(1, champ, brut, 'COMPTEUR_IMPRECIS', 'IMPRECISION');
}
/**
 * Au-delà, un compteur n'est pas un nombre de conjoints ou d'enfants : c'est
 * un matricule, un téléphone ou un salaire tombé dans la colonne. Mesuré le
 * 16/10 sur les DNS finales : un « nombre d'enfants » à dix chiffres passait
 * la lecture puis faisait échouer l'application (entier 32 bits dépassé).
 */
export const COMPTEUR_MAX = 30;
function borne(n, champ, brut) {
    return n > COMPTEUR_MAX ? avecAnomalie(null, champ, brut, 'COMPTEUR_ILLISIBLE') : sansAnomalie(n);
}
// ─────────────────────────────────────────────────────────────────────
//  Salaire — la base du recouvrement (RG-FIN-01)
// ─────────────────────────────────────────────────────────────────────
/**
 * Bornes de vraisemblance d'un salaire brut mensuel, en ariary. En dessous
 * de 1 000 Ar, c'est une case mal lue ou une unité oubliée (« 264 » pour
 * « 264 000 ») ; au-delà d'un milliard, deux cellules collées. Dans les deux
 * cas on ne retient RIEN : un salaire faux fausse une cotisation, et la
 * différence se découvre au moment de relancer l'entreprise.
 */
const SALAIRE_MIN = 1_000;
const SALAIRE_MAX = 1_000_000_000;
/**
 * Un montant tel qu'il s'écrit dans un tableur ou sur un DNS papier, en
 * ariary : `264 200,00`, `264.200,00`, `264,200.00`, `264200`, `264 200 Ar`.
 *
 * Le séparateur décimal se déduit de la forme, jamais de la langue du poste :
 * s'il y a deux séparateurs, le dernier est décimal ; s'il n'y en a qu'un,
 * il est décimal suivi d'un ou deux chiffres, et séparateur de milliers
 * suivi de trois. Tout le reste est illisible — et reste vide.
 */
export function normaliserMontant(brut) {
    if (brut === null || brut === undefined)
        return sansAnomalie(null);
    if (typeof brut === 'boolean' || brut instanceof Date) {
        return avecAnomalie(null, 'salaireBrut', brut, 'MONTANT_ILLISIBLE');
    }
    let valeur = null;
    if (typeof brut === 'number') {
        valeur = Number.isFinite(brut) ? brut : null;
    }
    else {
        const t = brut.replace(/[\s\u00a0\u202f]/gu, '').replace(/(ARIARY|MGA|AR)$/iu, '');
        if (t === '' || t === '-')
            return sansAnomalie(null);
        if (!/^\d[\d.,]*$/u.test(t)) {
            return avecAnomalie(null, 'salaireBrut', brut, 'MONTANT_ILLISIBLE');
        }
        const separateurs = t.replace(/\d/gu, '');
        let chiffres;
        if (separateurs === '') {
            chiffres = t;
        }
        else if (new Set(separateurs).size === 2) {
            // Deux séparateurs différents : le dernier est décimal.
            const decimal = t.lastIndexOf('.') > t.lastIndexOf(',') ? '.' : ',';
            const milliers = decimal === '.' ? ',' : '.';
            chiffres = t.split(milliers).join('').replace(decimal, '.');
        }
        else {
            const sep = separateurs[0] ?? '';
            const morceaux = t.split(sep);
            const dernier = morceaux[morceaux.length - 1] ?? '';
            if (morceaux.length === 2 && dernier.length >= 1 && dernier.length <= 2) {
                chiffres = morceaux.join('.'); // décimal
            }
            else if (morceaux.slice(1).every((m) => m.length === 3)) {
                chiffres = morceaux.join(''); // milliers
            }
            else {
                return avecAnomalie(null, 'salaireBrut', brut, 'MONTANT_ILLISIBLE');
            }
        }
        valeur = Number(chiffres);
    }
    if (valeur === null || !Number.isFinite(valeur) || valeur < SALAIRE_MIN || valeur > SALAIRE_MAX) {
        // Un zéro déclaré n'est pas une erreur : c'est « pas de salaire ce mois-ci ».
        if (valeur === 0)
            return sansAnomalie(0);
        return avecAnomalie(null, 'salaireBrut', brut, 'MONTANT_ILLISIBLE');
    }
    return sansAnomalie(Math.round(valeur * 100) / 100);
}
// ─────────────────────────────────────────────────────────────────────
//  Erreurs du tableur
// ─────────────────────────────────────────────────────────────────────
/**
 * Le texte d'une erreur d'Excel, en anglais ou en français : `#REF!`, `#N/A`,
 * `#VALEUR!`… Une formule en erreur est déjà lue comme vide ; ce même texte
 * resté après un « coller les valeurs » doit l'être aussi.
 *
 * Mesuré le 24/09/2026 sur 338 DNS : il arrivait jusqu'aux lignes lues dans
 * 6 fichiers — trois salariés nommés « #REF! », un matricule « #REF! »
 * partagé, 134 fonctions et lieux d'affectation.
 */
const ERREUR_TABLEUR = /^#(REF!|N\/A|NA|VALUE!|VALEUR!|DIV\/0!|NAME\?|NOM\?|NUM!|NOMBRE!|NULL!|NUL!|SPILL!|CALC!)$/i;
export function estErreurTableur(texte) {
    return ERREUR_TABLEUR.test(texte.trim());
}
/**
 * R7 (décision du TL, 16/10) — une cellule vide déguisée : « DATE INVALIDE »
 * (le texte qu'Excel laisse d'une date impossible) ou un tiret seul. Elle se
 * lit vide, comme `#REF!` ; le rapport dit combien il y en avait.
 */
export function estVideDeguise(texte) {
    return /^(DATE INVALIDE|-+|–|—)$/i.test(texte.trim());
}
/** En deçà, vers le rose et le violet, ce n'est plus du rouge. */
const TEINTE_ROUGE_MIN = -20;
/** Au-delà de l'orange d'Excel (`FFC000`, 45°), c'est du jaune. */
const TEINTE_ORANGE_MAX = 46;
/** Entre l'orange et le jaune : seulement franc, sinon c'est un surlignage pâle (`FFE699`). */
const TEINTE_AMBRE = 40;
const SATURATION_MIN = 0.4;
const SATURATION_AMBRE_MIN = 0.8;
/**
 * La couleur d'un départ (CP-03), sur la police ou sur le fond : ROUGE ou
 * ORANGE — la seule définition du projet, lecture et écran confondus.
 *
 * Décision du TL (29/09) : « un nom en rouge ou en orange ⇒ proposer
 * parti ». Rouges : `FF0000`, `C00000`, bordeaux, rouge clair. Oranges :
 * `FFC000` (l'« orange » d'Excel), `ED7D31`, `F4B084`, `FF9900`, et les
 * bruns orangés. Ni le rose, ni le jaune, ni un surlignage pâle, ni le vert
 * (`00B050`), ni le bleu (`002060`), ni le noir.
 *
 * Jugé sur la TEINTE, de -20° à 46°, assez claire (`R > 120`) et assez
 * saturée ; au-delà de 40°, entre orange et jaune, franche seulement.
 *
 * Ce n'est qu'une proposition : la chargée tranche, et seul le NOM compte
 * (voir `lireLignes`) — une remarque en rouge n'est pas un départ.
 */
export function estCouleurDepart(couleur) {
    if (couleur === null)
        return false;
    const { r, v, b } = couleur;
    const min = Math.min(v, b);
    if (r <= 120 || r <= Math.max(v, b))
        return false;
    const saturation = (r - min) / r;
    if (saturation < SATURATION_MIN)
        return false;
    const teinte = (60 * (v - b)) / (r - min);
    if (teinte < TEINTE_ROUGE_MIN || teinte > TEINTE_ORANGE_MAX)
        return false;
    return teinte <= TEINTE_AMBRE || saturation >= SATURATION_AMBRE_MIN;
}
/**
 * Une couleur « chaude » et franche — rouge, orange, ambre, rose, brun — de
 * celles qu'une entreprise pourrait avoir voulu dire « parti ». La règle
 * (`estCouleurDepart`) retient le rouge et l'orange ; les autres se MONTRENT
 * à la chargée, qui tranche. Le vert, le bleu, le gris et le jaune de surlignage
 * n'en sont pas.
 */
export function estCouleurChaude(couleur) {
    if (couleur === null)
        return false;
    const { r, v, b } = couleur;
    const min = Math.min(v, b);
    if (r <= 100 || r <= Math.max(v, b) || (r - min) / r < 0.4)
        return false;
    const teinte = (60 * (v - b)) / (r - min);
    return teinte >= -60 && teinte <= 50;
}
/** `RRGGBB`, pour montrer à l'écran la couleur telle que le fichier la porte. */
export function hexaDe(couleur) {
    return [couleur.r, couleur.v, couleur.b]
        .map((x) => x.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase();
}
/** `RRGGBB` ou `AARRGGBB` — les deux notations d'OOXML. */
export function couleurDepuisHexa(hexa) {
    if (typeof hexa !== 'string')
        return null;
    const propre = hexa.replace(/^#/, '').trim();
    const rvb = propre.length === 8 ? propre.slice(2) : propre;
    if (!/^[0-9a-fA-F]{6}$/.test(rvb))
        return null;
    return {
        r: parseInt(rvb.slice(0, 2), 16),
        v: parseInt(rvb.slice(2, 4), 16),
        b: parseInt(rvb.slice(4, 6), 16),
    };
}
//# sourceMappingURL=normalisation.js.map